import { Label } from '@folio/components/forms/label/label';
import { Textarea } from '@folio/components/forms/textarea';
import { Badge } from '@folio/components/ui/badge';
import { Button } from '@folio/components/ui/button';
import { Group } from '@folio/components/ui/flex';
import { Popover, PopoverContent, PopoverTrigger } from '@folio/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@folio/components/ui/radio-group';
import { Text, Title } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';
import { cn, formatDateWithTime } from '@folio/lib';
import type { CorrectPolicyDocumentFraudVerdictInputBody, DocumentFraudScoreDto } from 'korint-sdk';
import {
  ArrowRightLeftIcon,
  InfoIcon,
  Loader2Icon,
  ShieldAlertIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FRAUD_SEVERITY_TONE,
  FRAUD_STATUS_TONE,
  FRAUD_VERDICTS,
  TONE_TEXT_CLASS,
  effectiveFraudStatus,
  fraudSignalCategory,
  isFraudVerdict,
  sortFraudSignals,
} from '../fraudStatus';
import { FraudStatusBadge } from '../FraudStatusBadge';

type FraudVerdict = CorrectPolicyDocumentFraudVerdictInputBody['verdict'];

const SEVERITY_ICON = {
  critical: ShieldAlertIcon,
  warning: TriangleAlertIcon,
  info: InfoIcon,
};

export function FraudAnalysisSection({
  fraud,
  pending,
  onCorrectVerdict,
  isCorrectingVerdict,
}: {
  fraud?: DocumentFraudScoreDto | null;
  pending?: boolean;
  onCorrectVerdict?: (correction: CorrectPolicyDocumentFraudVerdictInputBody) => void;
  isCorrectingVerdict?: boolean;
}) {
  const { t } = useTranslation();
  const status = fraud ? effectiveFraudStatus(fraud) : undefined;
  const tone = status ? FRAUD_STATUS_TONE[status] : 'muted';
  const signals = fraud ? sortFraudSignals(fraud.signals) : [];
  const score =
    fraud && fraud.fraudScore !== null
      ? t('documentReview.fraud.scoreOutOf', { score: fraud.fraudScore })
      : undefined;

  return (
    <section className="py-5 first:pt-0 last:pb-0">
      <Group justify="between" gap={3} className="mb-3 flex-wrap">
        <Group gap={3}>
          <ThemeIcon tone={tone}>
            <ShieldAlertIcon />
          </ThemeIcon>
          <Title order={3} size="lg" tone="default" className="leading-7">
            {t('documentReview.fraud.title')}
          </Title>
        </Group>
        <Group gap={1} className="shrink-0">
          {fraud && <FraudStatusBadge fraud={fraud} />}
          {fraud && onCorrectVerdict && fraud.status !== 'pending' && (
            <FraudVerdictPopover
              fraud={fraud}
              onSubmit={onCorrectVerdict}
              isPending={isCorrectingVerdict}
            />
          )}
        </Group>
      </Group>

      {!fraud ? (
        pending && (
          <Group gap={2} className="text-muted-foreground">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            <Text size="sm" tone="muted">
              {t('documentReview.fraud.pending')}
            </Text>
          </Group>
        )
      ) : (
        <>
          {fraud.managerVerdict && (
            <div className="mb-4 space-y-1 rounded-md border border-border bg-muted/40 p-3">
              <Text size="sm" weight="semibold" tone="default">
                {t('documentReview.fraud.review.human')}
              </Text>
              {fraud.managerReviewedAt && (
                <Text size="xs" tone="muted">
                  {t(
                    fraud.managerReviewedByEmail
                      ? 'documentReview.fraud.review.reviewedBy'
                      : 'documentReview.fraud.review.reviewedAt',
                    {
                      email: fraud.managerReviewedByEmail,
                      date: formatDateWithTime(new Date(fraud.managerReviewedAt)),
                    },
                  )}
                </Text>
              )}
              {fraud.managerReviewNotes && (
                <Text size="sm" tone="default" className="whitespace-pre-wrap">
                  {fraud.managerReviewNotes}
                </Text>
              )}
              <Text size="xs" tone="muted">
                {t('documentReview.fraud.review.automatedVerdict', {
                  status: [t(`documentReview.fraud.status.${fraud.status}`), score]
                    .filter(Boolean)
                    .join(' · '),
                })}
              </Text>
            </div>
          )}
          <Text size="sm" tone="default">
            {t(`documentReview.fraud.summary.${fraud.status}`)}
          </Text>
          {fraud.fieldExtractionStatus === 'empty' && (
            <Text size="sm" tone="muted" className="mt-2">
              {t('documentReview.fraud.limitedCoverage')}
            </Text>
          )}

          {signals.length > 0 ? (
            <>
              <Text
                size="xs"
                weight="semibold"
                tone="muted"
                className="-mx-5 mt-4 bg-muted/60 px-5 py-2 uppercase tracking-wide"
              >
                {t('documentReview.fraud.findings')}{' '}
                <span className="font-normal">{signals.length}</span>
              </Text>
              <ol className="-mx-5 divide-y divide-border">
                {signals.map(({ code, message, severity = 'info' }, index) => {
                  const severityTone = FRAUD_SEVERITY_TONE[severity];
                  const SeverityIcon = SEVERITY_ICON[severity];
                  const category = fraudSignalCategory(code);
                  return (
                    <li key={index} className="flex gap-3 px-5 py-3">
                      <ThemeIcon
                        variant="filled"
                        tone={severityTone}
                        size="sm"
                        radius="full"
                        className="text-xs font-semibold"
                      >
                        {index + 1}
                      </ThemeIcon>
                      <div className="min-w-0 space-y-1">
                        <Group gap={2} className="flex-wrap">
                          <Text
                            inline
                            size="sm"
                            weight="semibold"
                            className={cn(
                              'inline-flex items-center gap-1',
                              TONE_TEXT_CLASS[severityTone],
                            )}
                          >
                            <SeverityIcon className="size-4" />
                            {t(`documentReview.fraud.severity.${severity}`)}
                          </Text>
                          {category && (
                            <Badge variant="light" tone="muted" size="sm">
                              {t(`documentReview.fraud.category.${category}`)}
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" tone="default">
                          {message ?? code}
                        </Text>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : (
            isFraudVerdict(fraud.status) && (
              <Text size="sm" tone="muted" className="mt-2">
                {t('documentReview.fraud.noFindings')}
              </Text>
            )
          )}
        </>
      )}
    </section>
  );
}

function FraudVerdictPopover({
  fraud,
  onSubmit,
  isPending,
}: {
  fraud: DocumentFraudScoreDto;
  onSubmit: (correction: CorrectPolicyDocumentFraudVerdictInputBody) => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded text-slate-500 hover:bg-slate-100"
          title={t('documentReview.fraud.review.formTitle')}
          aria-label={t('documentReview.fraud.review.formTitle')}
          aria-busy={isPending}
          disabled={isPending}
          type="button"
        >
          {isPending ? (
            <Loader2Icon className="h-3 w-3 animate-spin" />
          ) : (
            <ArrowRightLeftIcon className="h-3 w-3" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <FraudVerdictForm
          fraud={fraud}
          onSubmit={correction => {
            onSubmit(correction);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function FraudVerdictForm({
  fraud,
  onSubmit,
}: {
  fraud: DocumentFraudScoreDto;
  onSubmit: (correction: CorrectPolicyDocumentFraudVerdictInputBody) => void;
}) {
  const { t } = useTranslation();
  const [verdict, setVerdict] = useState<FraudVerdict | undefined>(
    fraud.managerVerdict ?? undefined,
  );
  const [notes, setNotes] = useState(fraud.managerReviewNotes ?? '');

  return (
    <div className="space-y-3">
      <Text size="sm" weight="semibold" tone="default">
        {t('documentReview.fraud.review.formTitle')}
      </Text>
      <RadioGroup
        value={verdict ?? ''}
        onValueChange={value => setVerdict(value as FraudVerdict)}
        aria-label={t('documentReview.fraud.review.formTitle')}
      >
        {FRAUD_VERDICTS.map(option => (
          <Group key={option} gap={2}>
            <RadioGroupItem value={option} id={`fraud-verdict-${option}`} />
            <Label htmlFor={`fraud-verdict-${option}`}>
              {t(`documentReview.fraud.status.${option}`)}
            </Label>
          </Group>
        ))}
      </RadioGroup>
      <Textarea
        aria-label={t('documentReview.fraud.review.notesLabel')}
        placeholder={t('documentReview.fraud.review.notesPlaceholder')}
        value={notes}
        onChange={event => setNotes(event.target.value)}
      />
      <Group justify="end">
        <Button
          size="sm"
          disabled={!verdict}
          onClick={() =>
            verdict &&
            onSubmit({
              verdict,
              notes: notes.trim() || undefined,
              expectedUpdatedAt: fraud.updatedAt,
            })
          }
        >
          {t('documentReview.fraud.review.submit')}
        </Button>
      </Group>
    </div>
  );
}
